import { Button, Dialog } from "@au-schein-de/au-ui-library";
import * as Sentry from "@sentry/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TransformedImage } from "~/components/transformed-image.component";
import { useError } from "~/hooks/use-error.hook";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import { errorViewedEvent } from "~/tooling/telemetry/events";

const ERROR_SRC = "/images/error.png";

export const ErrorDialog = () => {
	const { t } = useTranslation();
	const { error, hideError } = useError();

	useEffect(() => {
		if (!error) return;

		const message = error.message ?? error.title ?? "Unknown error";
		if (
			error.telemetry?.category !== "notice" &&
			error.telemetry?.category !== "validation"
		) {
			Sentry.captureMessage(`Error Dialog: ${message}`, {
				level: "error",
				tags: { error_category: error.telemetry?.category ?? "unknown" },
				extra: { title: error.title, message: error.message },
			});
		}

		const traceId = Sentry.getActiveSpan()?.spanContext().traceId;
		captureEvent(
			errorViewedEvent(error.errorId ?? traceId, message, error.telemetry),
		);
	}, [error]);

	if (!error) return null;

	const onOpenChange = (open: boolean) => {
		if (!open) hideError();
	};

	return (
		<Dialog.Root open={!!error} onOpenChange={onOpenChange}>
			<Dialog.Content withCloseButton={false}>
				<Dialog.Header className="flex flex-col items-start gap-4">
					<TransformedImage
						src={ERROR_SRC}
						alt=""
						width={96}
						height={96}
						className="size-24 shrink-0 object-contain"
					/>
					<div className="flex w-full flex-col gap-2">
						<Dialog.Title className="text-h3 text-gray-900">
							{error.title ?? t("errors.somethingWentWrong")}
						</Dialog.Title>
						<Dialog.Description>
							<span className="text-body text-gray-700">{error.message}</span>
						</Dialog.Description>
					</div>
				</Dialog.Header>

				<Dialog.Footer>
					<div className="flex w-full flex-col gap-2">
						{error.primaryButton && (
							<Button
								size="md"
								variant="primary"
								className="w-full"
								onClick={error.primaryButton.action}
							>
								{error.primaryButton.label}
							</Button>
						)}

						{error.secondaryButton && (
							<Button
								size="md"
								variant="secondary"
								className="w-full"
								onClick={error.secondaryButton.action}
							>
								{error.secondaryButton.label}
							</Button>
						)}
					</div>
				</Dialog.Footer>
			</Dialog.Content>
		</Dialog.Root>
	);
};
